import { FeatureFlagsContext } from '@artic/ui/feature-flags';
import { type ReactNode } from 'react';

import { useFeatureFlagsControllerGetFlagsSuspense } from './generated/feature-flags/feature-flags.ts';
import type { FeatureFlagsResponseDto } from './generated/schemas/index.ts';

interface FeatureFlagProviderProps {
  children: ReactNode;
  partnerId: string | undefined;
}

/**
 * Fetches feature flags for the given partner and provides them via
 * `FeatureFlagsContext` from `@artic/ui`.
 *
 * Must be rendered inside a `<QueryClientProvider>` and wrapped with a
 * `<Suspense>` boundary. When `partnerId` is undefined, all flags default to off.
 */
export function FeatureFlagProvider({ children, partnerId }: FeatureFlagProviderProps) {
  if (!partnerId) {
    return <FeatureFlagsContext.Provider value={{}}>{children}</FeatureFlagsContext.Provider>;
  }

  return <FeatureFlagProviderInner partnerId={partnerId}>{children}</FeatureFlagProviderInner>;
}

function FeatureFlagProviderInner({
  children,
  partnerId,
}: {
  children: ReactNode;
  partnerId: string;
}) {
  const { data: response } = useFeatureFlagsControllerGetFlagsSuspense({ partner_id: partnerId });

  const dto = response.data as FeatureFlagsResponseDto;

  return <FeatureFlagsContext.Provider value={dto.flags}>{children}</FeatureFlagsContext.Provider>;
}
